import React, { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Tab, Tabs } from '@mui/material';
import { PageHeader } from '../components/ui';
import TeamCheckinPanel from '../components/attendance/TeamCheckinPanel';
import TeamWeekendPanel from '../components/attendance/TeamWeekendPanel';

type TeamAttendanceTab = 'checkin' | 'weekend';

const sameParts = (left: string[] | undefined, right: string[]) =>
  left !== undefined && left.length === right.length && left.every((part, index) => part === right[index]);

/**
 * Read-only view of a manager's team: morning office check-ins and weekend
 * shifts. The server scopes every request to the requester's team, so this
 * page carries no client-side scoping and offers no write actions.
 */
const TeamAttendance: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: TeamAttendanceTab = searchParams.get('tab') === 'weekend' ? 'weekend' : 'checkin';

  // Each view owns its data; it reports the header's scope · period · freshness
  // here from a layout effect, with placeholders until its data arrives, so the
  // header paints once at a stable length.
  const [metaByTab, setMetaByTab] = useState<Partial<Record<TeamAttendanceTab, string[]>>>({});
  const reportCheckinMeta = useCallback(
    (parts: string[]) => setMetaByTab((prev) => (sameParts(prev.checkin, parts) ? prev : { ...prev, checkin: parts })),
    []
  );
  const reportWeekendMeta = useCallback(
    (parts: string[]) => setMetaByTab((prev) => (sameParts(prev.weekend, parts) ? prev : { ...prev, weekend: parts })),
    []
  );
  const meta = metaByTab[tab];

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader overline="Attendance" title="Team attendance" meta={meta}>
        <Tabs
          value={tab}
          onChange={(_event, value: TeamAttendanceTab) =>
            setSearchParams(value === 'checkin' ? {} : { tab: value }, { replace: true })
          }
          aria-label="Team attendance views"
          variant="scrollable"
        >
          <Tab value="checkin" label="Morning check-in" id="team-attendance-tab-checkin" aria-controls="team-attendance-panel" />
          <Tab value="weekend" label="Weekend shifts" id="team-attendance-tab-weekend" aria-controls="team-attendance-panel" />
        </Tabs>
      </PageHeader>

      <Box role="tabpanel" id="team-attendance-panel" aria-labelledby={`team-attendance-tab-${tab}`} sx={{ minWidth: 0 }}>
        {tab === 'checkin' ? (
          <TeamCheckinPanel onMetaChange={reportCheckinMeta} />
        ) : (
          <TeamWeekendPanel onMetaChange={reportWeekendMeta} />
        )}
      </Box>
    </Box>
  );
};

export default TeamAttendance;
